import React, { useEffect, useState, useRef } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { useNavigate, useLocation, Routes, Route } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ArrowRight, User, Radio, Banknote, LogOut, Globe, AlertCircle, X, Check, MessageCircle, Mic, Tv, Settings, QrCode, HelpCircle } from 'lucide-react';
import { useAuth } from '@/contexts/FirebaseAuthContext';
import { useMobile } from '@/features/shared/contexts/MobileContext';
import { useToast } from '@/components/ui/use-toast';
import { db } from '@/lib/firebase';
import { config } from '@/lib/config';

import QRCode from 'qrcode.react';

// Import Karaoke-specific pages
import KaraokeLiveDashboardPage from './KaraokeLiveDashboardPage';
import KaraokeProfilePage from './KaraokeProfilePage';

import KaraokePayoutsPage from './KaraokePayoutsPage';
import KaraokeQuickTour from './KaraokeQuickTour';

const SidebarWrapper = ({ children, className = "" }) => (
  <div className={`h-full overflow-auto ${className}`}>
    {children}
  </div>
);

const KaraokeAdminDashboard = () => {
  const { user, profile, signOut, loading: authLoading } = useAuth();
  const mobile = useMobile();
  const { toast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();


  const [currentPanel, setCurrentPanel] = useState('/karaoke/admin/live-dashboard');
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [welcomeMessageDismissed, setWelcomeMessageDismissed] = useState(false);
  const [showMelody, setShowMelody] = useState(false);
  const [showTour, setShowTour] = useState(false);

  // Load welcome message dismissal state from localStorage (user-specific)
  useEffect(() => {
    if (user?.uid) {
      const dismissed = localStorage.getItem(`karaoke-welcome-message-dismissed-${user.uid}`);
      if (dismissed === 'true') {
        setWelcomeMessageDismissed(true);
      }
    }
  }, [user?.uid]);

  // Check if tour should be auto-triggered
  useEffect(() => {
    if (user?.uid && !authLoading) {
      const shouldShowTour = localStorage.getItem(`karaoke-show-tour-${user.uid}`);
      if (shouldShowTour === 'true') {
        setShowTour(true);
        // Clear the flag so it doesn't trigger again
        localStorage.removeItem(`karaoke-show-tour-${user.uid}`);
      }
    }
  }, [user?.uid, authLoading]);

  // Handle dismissing welcome message
  const handleDismissWelcome = () => {
    setWelcomeMessageDismissed(true);
    if (user?.uid) {
      localStorage.setItem(`karaoke-welcome-message-dismissed-${user.uid}`, 'true');
    }
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/karaoke/login');
  };

  const handleTourComplete = () => {
    setShowTour(false);
    navigate('/karaoke/admin/live-dashboard');
    toast({
      title: "Tour Complete!",
      description: "You're ready to start your first karaoke session!",
      variant: "default"
    });
  };

  const handleTourSkip = () => {
    setShowTour(false);
    toast({
      title: "Tour Skipped",
      description: "You can always access the tour from the dashboard anytime.",
      variant: "default"
    });
  };

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };
  
  const handlePanelChange = (panelPath) => {
    if (currentPanel === panelPath) return;
    setCurrentPanel(panelPath);
    navigate(panelPath);
  };



  // Check setup requirements
  const needsUrlSetup = user && profile !== undefined && !profile?.url_slug;

  // Karaoke-specific sidebar items
  const sidebarItems = [
    {
      path: '/karaoke/admin/live-dashboard',
      title: 'Live Dashboard',
      description: 'Manage active karaoke sessions',
      icon: Radio,
      color: 'from-red-500 to-orange-500',
      disabled: needsUrlSetup
    },
    {
      path: '/karaoke/admin/profile',
      title: 'Profile',
      description: 'Manage your karaoke profile',
      icon: User,
      color: 'from-purple-500 to-pink-500',
      disabled: false
    },

    {
      path: '/karaoke/admin/payouts',
      title: 'Payouts',
      description: 'Track your earnings',
      icon: Banknote,
      color: 'from-yellow-500 to-orange-500',
      disabled: needsUrlSetup
    },
    {
      path: `${config.getBaseUrl()}/karaoke/${profile?.url_slug}/tv`,
      title: 'TV Display',
      description: 'Open TV display',
      icon: Tv,
      color: 'from-cyan-500 to-blue-500',
      disabled: needsUrlSetup,
      isExternal: true
    },
    {
      path: `${config.getBaseUrl()}/karaoke/${profile?.url_slug}/tv-control`,
      title: 'TV Control',
      description: 'Control TV display',
      icon: Settings,
      color: 'from-gray-500 to-slate-500',
      disabled: needsUrlSetup,
      isExternal: true
    }
  ];

  const mobilePrimaryItems = sidebarItems.filter((item) => !item.isExternal);
  const mobileQuickLinks = mobile.isMobile ? [] : sidebarItems.filter((item) => item.isExternal);

  // Calculate highlight position for active item
  const activeIndex = sidebarItems.findIndex(item => item.path === currentPanel);
  const EXPANDED_ITEM_HEIGHT = 60; // px
  const EXPANDED_STEP = 68;        // item height + 8px gap
  const COLLAPSED_ITEM_HEIGHT = 48; // px
  const COLLAPSED_STEP = 56;        // item height + 8px gap
  const highlightY = activeIndex * (sidebarCollapsed ? COLLAPSED_STEP : EXPANDED_STEP);
  const highlightH = sidebarCollapsed ? COLLAPSED_ITEM_HEIGHT : EXPANDED_ITEM_HEIGHT;

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-950 via-purple-900 to-indigo-950 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-400 mx-auto mb-4"></div>
          <p className="text-purple-300">Loading Karaoke Dashboard...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    navigate('/karaoke/login');
    return null;
  }

  return (
    <>
      <Helmet>
        <title>Karaoke Admin Dashboard - Your Song Request</title>
        <meta name="description" content="Manage your karaoke sessions, profile, and promotional materials." />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-br from-purple-950 via-purple-900 to-indigo-950 text-white overflow-x-hidden">
        {/* Welcome Message */}
        {!welcomeMessageDismissed && profile?.url_slug && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 max-w-md w-full mx-4"
          >
            <div className="bg-purple-900/90 backdrop-blur-sm border border-purple-700/50 rounded-lg p-4 shadow-xl">
              <div className="flex items-start space-x-3">
                <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center flex-shrink-0">
                  <Mic className="w-4 h-4 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="text-purple-200 font-semibold mb-1">Welcome to Karaoke Mode!</h3>
                  <p className="text-purple-300 text-sm mb-3">
                    Ready to rock the mic? Take our Quick Guide Wizard to learn how to run amazing karaoke sessions!
                  </p>
                  <div className="flex space-x-2">
                    <Button
                      onClick={() => {
                        handleDismissWelcome();
                        // Directly trigger the tour
                        setShowTour(true);
                      }}
                      size="sm"
                      className="bg-purple-600 hover:bg-purple-700"
                    >
                      Start Quick Guide
                    </Button>
                    <Button
                      onClick={handleDismissWelcome}
                      variant="outline"
                      size="sm"
                      className="border-purple-600 text-purple-300 hover:bg-purple-600/20"
                    >
                      Skip for Now
                    </Button>
                  </div>
                </div>
                <button
                  onClick={handleDismissWelcome}
                  className="text-purple-400 hover:text-purple-300"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>
          </motion.div>
        )}

        <div className={`flex h-screen ${mobile.isMobile ? 'flex-col' : ''}`}>
          {/* Sidebar (desktop only) */}
          {!mobile.isMobile && (
          <aside className={`relative ${sidebarCollapsed ? 'w-20' : 'w-80'} bg-purple-900/30 backdrop-blur-sm border-r border-purple-800/50 flex flex-col transition-all duration-300 ease-in-out`}>
            {/* Header */}
            <div className="p-6 border-b border-purple-800/50">
              <div className="flex items-center space-x-3">
                {sidebarCollapsed && (
                  <div className="flex justify-center">
                    <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                      <span className="text-white font-bold text-sm">K</span>
                    </div>
                  </div>
                )}
                {!sidebarCollapsed && (
                  <>
                    <div className="w-10 h-10 bg-gradient-to-r from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                      <Mic className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <h1 className="text-lg font-bold text-purple-100">Karaoke Mode</h1>
                      <p className="text-xs text-purple-400">Your Song Request</p>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Sidebar navigation */}
            <div className="p-4 flex-1">
              <nav className="relative mt-2">
                <motion.div
                  className="absolute inset-x-0 bg-gradient-to-r from-purple-600/20 to-pink-600/20 border border-purple-500/30 shadow-lg rounded-lg"
                  animate={{ y: highlightY, height: highlightH }}
                  transition={{ type: "spring", stiffness: 300, damping: 30 }}
                />
                <div className="relative space-y-2">
                  {sidebarItems.map((item, index) => (
                    <button
                      key={item.path}
                      onClick={() => !item.disabled && (item.isExternal ? window.open(item.path, '_blank') : handlePanelChange(item.path))}
                      className={`relative block w-full text-left rounded-lg transition-all duration-200 group ${sidebarCollapsed ? 'p-3 h-12' : 'p-3 h-[60px]'} ${item.disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-purple-800/50'}`}
                    >
                      <div className="flex items-center space-x-3">
                        <div className={`w-9 h-9 rounded-md bg-gradient-to-r ${item.color} flex items-center justify-center ${sidebarCollapsed ? 'mx-auto' : ''}`}>
                          <item.icon className="w-4 h-4 text-white" />
                        </div>
                        {!sidebarCollapsed && (
                          <div className="flex flex-col">
                            <span className={`font-medium ${item.disabled ? 'text-purple-600' : currentPanel === item.path ? 'text-white' : 'text-purple-200 group-hover:text-white'}`}>{item.title}</span>
                            {currentPanel === item.path && (
                              <span className={`text-xs ${item.disabled ? 'text-purple-700' : 'text-purple-400'} truncate max-w-[200px]`}>{item.description}</span>
                            )}
                          </div>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </nav>
            </div>
            
            {/* Sidebar footer with Melody, tour, and logout */}
            <div className="p-4 border-t border-purple-800 space-y-2">
              <button
                onClick={() => setShowMelody(true)}
                className={`w-full flex items-center space-x-3 p-3 rounded-lg transition-all duration-200 hover:bg-purple-900/20 hover:border-purple-700/50 border border-transparent ${sidebarCollapsed ? 'justify-center' : ''}`}
              >
                <MessageCircle className={`w-5 h-5 text-purple-400 ${sidebarCollapsed ? 'mx-auto' : ''}`} />
                {!sidebarCollapsed && <span className="text-purple-400 font-medium">Melody</span>}
              </button>
              <button
                onClick={() => setShowTour(true)}
                className={`w-full flex items-center space-x-3 p-3 rounded-lg transition-all duration-200 hover:bg-purple-900/20 hover:border-purple-700/50 border border-transparent ${sidebarCollapsed ? 'justify-center' : ''}`}
                title="Quick Guide Wizard - Learn how to use Karaoke Mode"
              >
                <HelpCircle className={`w-5 h-5 text-purple-400 ${sidebarCollapsed ? 'mx-auto' : ''}`} />
                {!sidebarCollapsed && <span className="text-purple-400 font-medium">Quick Guide</span>}
              </button>
              <button
                onClick={() => setShowLogoutConfirm(true)}
                className={`w-full flex items-center space-x-3 p-3 rounded-lg transition-all duration-200 hover:bg-red-900/20 hover:border-red-700/50 border border-transparent ${sidebarCollapsed ? 'justify-center' : ''}`}
              >
                <LogOut className={`w-5 h-5 text-red-400 ${sidebarCollapsed ? 'mx-auto' : ''}`} />
                {!sidebarCollapsed && <span className="text-red-400 font-medium">Logout</span>}
              </button>
            </div>
            
            {/* Sidebar collapse toggle */}
            <button
              onClick={toggleSidebar}
              className="absolute -right-3 top-1/2 transform -translate-y-1/2 bg-purple-800 hover:bg-purple-700 p-1 rounded-full border border-purple-700"
            >
              <ArrowRight className={`w-4 h-4 text-purple-400 transition-transform duration-300 ${!sidebarCollapsed ? 'rotate-180' : ''}`} />
            </button>
          </aside>
          )}

          <main className="flex-1 overflow-y-auto min-h-0">
            {mobile.isMobile && (
              <div className="sticky top-0 z-20 bg-purple-950/95 backdrop-blur-md border-b border-purple-800/50 p-3">
                <div className="flex gap-2 overflow-x-auto pb-1">
                  {mobilePrimaryItems.map((item) => (
                    <Button
                      key={item.path}
                      size="sm"
                      variant={currentPanel === item.path ? 'default' : 'outline'}
                      onClick={() => !item.disabled && handlePanelChange(item.path)}
                      disabled={item.disabled}
                      className={
                        currentPanel === item.path
                          ? 'bg-purple-600 hover:bg-purple-700 whitespace-nowrap'
                          : 'border-purple-700 text-purple-200 hover:bg-purple-800/40 whitespace-nowrap'
                      }
                    >
                      <item.icon className="w-4 h-4 mr-1.5" />
                      {item.title}
                    </Button>
                  ))}
                </div>
                <div className="flex gap-2 overflow-x-auto">
                  {mobileQuickLinks.map((item) => (
                    <Button
                      key={item.path}
                      size="sm"
                      variant="outline"
                      disabled={item.disabled}
                      onClick={() => !item.disabled && window.open(item.path, '_blank')}
                      className="border-purple-700 text-purple-200 hover:bg-purple-800/40 whitespace-nowrap"
                    >
                      <item.icon className="w-4 h-4 mr-1.5" />
                      {item.title}
                    </Button>
                  ))}
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setShowTour(true)}
                    className="border-purple-700 text-purple-200 hover:bg-purple-800/40 whitespace-nowrap"
                  >
                    <HelpCircle className="w-4 h-4 mr-1.5" />
                    Quick Guide
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setShowLogoutConfirm(true)}
                    className="border-red-700 text-red-300 hover:bg-red-900/30 whitespace-nowrap"
                  >
                    <LogOut className="w-4 h-4 mr-1.5" />
                    Logout
                  </Button>
                </div>
              </div>
            )}

            <div className={mobile.isMobile ? 'p-4' : 'p-6'}>
              {/* Setup required messages */}
              {needsUrlSetup && (
                <motion.div 
                  initial={{ opacity: 0, y: 20 }} 
                  animate={{ opacity: 1, y: 0 }} 
                  className="mb-6 p-6 bg-purple-900/20 border border-purple-700/50 rounded-lg"
                >
                  <div className="flex items-start space-x-4">
                    <AlertCircle className="w-6 h-6 text-purple-400 mt-1 flex-shrink-0" />
                    <div>
                      <h3 className="text-lg font-semibold text-purple-300 mb-2">Setup Required: Karaoke URL</h3>
                      <p className="text-purple-200 mb-4">
                        You need to set up your karaoke URL before you can access most dashboard features. 
                        This will be your public page where people can join your karaoke sessions.
                      </p>
                      <Button 
                        onClick={() => handlePanelChange('/karaoke/admin/profile')}
                        className="bg-purple-600 hover:bg-purple-700 text-white"
                      >
                        Set Up Profile
                      </Button>
                    </div>
                  </div>
                </motion.div>
              )}



              {/* Main content area */}
              <Routes>
                <Route path="/live-dashboard" element={<KaraokeLiveDashboardPage />} />
                <Route path="/profile" element={<KaraokeProfilePage />} />

                <Route path="/payouts" element={<KaraokePayoutsPage />} />
                <Route path="/" element={<KaraokeLiveDashboardPage />} />
              </Routes>
            </div>
          </main>
        </div>

        {/* Logout Confirmation Dialog */}
        <Dialog open={showLogoutConfirm} onOpenChange={setShowLogoutConfirm}>
          <DialogContent className="bg-purple-900/95 border-purple-700 max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-purple-100">Confirm Logout</DialogTitle>
              <DialogDescription className="text-purple-300">
                Are you sure you want to logout from Karaoke Mode?
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowLogoutConfirm(false)}
                className="border-purple-600 text-purple-300 hover:bg-purple-600/20"
              >
                Cancel
              </Button>
              <Button
                onClick={handleLogout}
                className="bg-red-600 hover:bg-red-700"
              >
                Logout
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Melody Chatbot Dialog */}
        <Dialog open={showMelody} onOpenChange={setShowMelody}>
          <DialogContent className="bg-purple-900/95 border-purple-700 max-w-2xl max-h-[85vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-purple-100">Melody - Karaoke Assistant</DialogTitle>
              <DialogDescription className="text-purple-300">
                Your AI assistant for karaoke management
              </DialogDescription>
            </DialogHeader>
            <div className="min-h-[400px] bg-purple-800/20 rounded-lg p-4">
              <p className="text-purple-300 text-center py-8">
                Melody chatbot integration coming soon for Karaoke Mode!
              </p>
            </div>
          </DialogContent>
        </Dialog>

        {/* Quick Tour Modal */}
        {showTour && (
          <KaraokeQuickTour
            onComplete={handleTourComplete}
            onSkip={handleTourSkip}
          />
        )}
      </div>
    </>
  );
};

export default KaraokeAdminDashboard;
